import {Nullish} from 'shared/web';

import {EagGridState} from '../createEagGridStore';

/**
 * Resize state read once per row by `GridRow`, passed to each `GridCell` as `isBeingResized`.
 *
 * Drag state (`activeDragId`, `overColumnId`, `dropSide`) is intentionally not
 * included here. Drag visuals (source-column opacity, drop-target background,
 * drop-side indicator) are applied via a `<style>` element injected by
 * `useGridDragAndDrop`, scoped by the `data-column` attribute on each `<td>`.
 * Keeping drag state out of cell subscriptions prevents N×M re-renders on
 * every dnd-kit `over` transition during a column drag.
 *
 * The selector caches the previous result and returns the same reference when
 * both fields are unchanged (`===`). Safe with `useSyncExternalStore`'s
 * `Object.is` comparison.
 */
export type CellResizeState = {
  isResizing: boolean;
  resizingColumn: string | Nullish;
};

// Per-snapshot WeakMap: for a given state object the selector always returns the
// same reference, even when multiple grid instances with different resize states
// share this module. A single mutable slot would let each instance's getSnapshot
// evict the other's cached value, making both snapshots change on every check —
// useSyncExternalStore then force-rerenders in an endless ping-pong (same failure
// class as useActionsDefinition, T20-135733).
const cache = new WeakMap<EagGridState, CellResizeState>();

// Last computed value, reused (by value equality) across state updates so
// consumers keep a stable reference while the resize fields are unchanged.
let lastResult: CellResizeState = {
  isResizing: false,
  resizingColumn: null,
};

export const selectCellResizeState = (state: EagGridState): CellResizeState => {
  const cached = cache.get(state);
  if (cached !== undefined) {
    return cached;
  }

  if (
    state.isResizing !== lastResult.isResizing ||
    state.resizingColumn !== lastResult.resizingColumn
  ) {
    lastResult = {
      isResizing: state.isResizing,
      resizingColumn: state.resizingColumn,
    };
  }

  cache.set(state, lastResult);
  return lastResult;
};
